/**
 * @React
 */
import React from "react";
/**
 * @MUI
 */
import { Box, TextField, IconButton, Button } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from "@mui/icons-material/Delete";
/**
 * @RHF
 */
import { useFieldArray, Controller, Control, useWatch } from "react-hook-form";
/**
 * @Components
 */
import FileUploadCGS from "../../../CustomComponents/FileUploadCGS";
import FileDownloader from "../../../CustomComponents/FileDownloadCGS";

interface DynamicFieldArrayProps {
    control: Control<any>;
    field_name: string;
    setValue: (name: string, value: any, options?: any) => void;
}

const DynamicFieldArray: React.FC<DynamicFieldArrayProps> = ({
    control,
    field_name,
    setValue,
}) => {
    const fieldArrayName = `attached_documents.${field_name}`;

    const { fields, append, remove } = useFieldArray({
        control,
        name: fieldArrayName,
    });

    const watchedValues = useWatch({
        control,
        name: fieldArrayName,
    });

    return (
        <>
            {fields.map((field, index) => {
                const fileNameValue = watchedValues?.[index]?.file_name;
                const filePathValue = watchedValues?.[index]?.path;
                const fileSignalPathValue = watchedValues?.[index]?.signed_url;
                const isUploaderDisabled = !fileNameValue?.trim();
                return (
                    <Box
                        key={field.id}
                        sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}
                    >
                        {/* File name */}
                        <Controller
                            name={`${fieldArrayName}.${index}.file_name`}
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    size="small"
                                    placeholder="დოკუმენტის სახელი"
                                    sx={{
                                        minWidth: 300,
                                        width: 350,
                                        "& .MuiOutlinedInput-root": {
                                            "&:hover fieldset": {
                                                color: "#1976d2",
                                                borderColor: "#1976d2",
                                                borderWidth: "2px",
                                            },
                                        },
                                    }}
                                    InputLabelProps={{
                                        sx: {
                                            fontSize: "1rem",
                                            top: '-2px',
                                        },
                                        shrink: true,
                                    }}
                                    InputProps={{
                                        sx: {
                                            // color: "rgba(0, 0, 0, 0.8)",
                                            height: '40px',
                                            padding: '2px',
                                        },
                                        inputProps: {
                                            fontSize: 12,
                                            transition: "font-size 0.2s, color 0.2s",
                                            min: 0, // Set the minimum value to 0
                                            style: {
                                                fontSize: 12,
                                            },
                                            color: "rgba(0, 0, 0, 0.87)",
                                            step: "1",
                                            padding: "10px",
                                        }
                                    }}
                                />
                            )}
                        />

                        {/* Upload / Download */}
                        {fileSignalPathValue && filePathValue ? (
                            <FileDownloader url={fileSignalPathValue} />
                        ) : (
                            <Controller
                                name={`${fieldArrayName}.${index}.path`}
                                control={control}
                                defaultValue={null}
                                render={({ field }) => {
                                    return (
                                        <FileUploadCGS
                                            uploadUrl="/bank/application/upload_attachments/monitoring-cgs"
                                            fieldName={field_name}
                                            disabled={isUploaderDisabled}
                                            onUploadSuccess={(data) => {
                                                const file = data[field_name]?.[0];
                                                if (!file) return;

                                                setValue(
                                                    `${fieldArrayName}.${index}.path`,
                                                    file.original_path,
                                                    { shouldDirty: true }
                                                );

                                                setValue(
                                                    `${fieldArrayName}.${index}.signed_url`,
                                                    file.signed_url,
                                                    { shouldDirty: true }
                                                );
                                                setValue(
                                                    `${fieldArrayName}.${index}.original_path`,
                                                    file.original_path,
                                                    { shouldDirty: true }
                                                );
                                            }}
                                        />
                                    )
                                }}
                            />
                        )}

                        {/* Remove */}
                        <IconButton size="small" onClick={() => remove(index)}>
                            <DeleteIcon sx={{ fontSize: 20, color: "#1976d2" }} />
                        </IconButton>
                    </Box>
                );
            })}

            {/* Add */}
            <Box display="flex" justifyContent="flex-end">
                <IconButton
                    size="small"
                    sx={{ width: "20px", height: "20px", p: 0 }}
                    onClick={() => append({ file_name: "", path: null })}
                >
                    <AddIcon sx={{ fontSize: 20, color: "#1976d2" }} />
                </IconButton>
            </Box>
        </>
    );
};


export default DynamicFieldArray;
